import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  aiNotifConfigGetir, aiNotifConfigKaydet,
  aiNotifSabahOzetiCalistir, aiNotifAcilUyariCalistir,
  aiNotifHaftalikOzetCalistir, aiNotifAkillıIpucuCalistir,
  aiNotifLoglarGetir, aiNotifStatsGetir,
} from "../services/assistantNotificationAdminService";
import "../styles/aiNotifAdmin.css";
import { useLanguage } from "../i18n/LanguageContext";

const DAYS_TR = [
  { key: "mon", label: "Pzt" }, { key: "tue", label: "Sal" },
  { key: "wed", label: "Çrş" }, { key: "thu", label: "Per" },
  { key: "fri", label: "Cum" }, { key: "sat", label: "Cmt" },
  { key: "sun", label: "Paz" },
];
const DAYS_EN = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" }, { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];
const ALL_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri"];
const WEEKEND_DAYS = ["sat", "sun"];
const WEEK_DAYS_TR = [
  { key: "mon", label: "Pazartesi" }, { key: "tue", label: "Salı" },
  { key: "wed", label: "Çarşamba" }, { key: "thu", label: "Perşembe" },
  { key: "fri", label: "Cuma" },     { key: "sat", label: "Cumartesi" },
  { key: "sun", label: "Pazar" },
];
const TYPE_LABELS = {
  daily_summary: "☀️ Sabah Özeti",
  urgent_alert:  "🚨 Acil Uyarı",
  weekly_digest: "📊 Haftalık Özet",
  smart_insight: "💡 Akıllı İpucu",
};

const DEFAULT_CONFIG = {
  enabled: true,
  global: { dailyLimitPerUser: 3, quietHoursStart: "22:00", quietHoursEnd: "08:00", timezone: "recipient_local" },
  dailySummary:  { enabled: true,  time: "08:30", days: ["mon","tue","wed","thu","fri","sat","sun"] },
  urgentAlerts:  { enabled: true,  checkIntervalHours: 2, maxPerDay: 2, cooldownHours: 20 },
  weeklyDigest:  { enabled: true,  day: "sun", time: "19:00" },
  smartInsights: { enabled: true, time: "12:00", maxPerWeek: 3, cooldownHours: 48 },
};

function Toggle({ on, onChange }) {
  return (
    <div className={`toggleSwitch${on ? " on" : ""}`} onClick={() => onChange(!on)}>
      <div className="toggleThumb" />
    </div>
  );
}
function TimeInput({ value, onChange }) {
  return (
    <input type="time" className="darkInput" value={value || "08:30"}
      onChange={e => onChange(e.target.value)} style={{ width: 110 }} />
  );
}
function NumberInput({ value, onChange, min = 1, max = 10 }) {
  return (
    <input type="number" className="darkInput" value={value ?? 1}
      min={min} max={max} onChange={e => onChange(Number(e.target.value || min))}
      style={{ width: 70 }} />
  );
}

function sameDays(left = [], right = []) {
  return left.length === right.length && right.every(day => left.includes(day));
}

function scheduleFrequency(days = []) {
  if (sameDays(days, ALL_DAYS)) return "daily";
  if (sameDays(days, WEEKDAYS)) return "weekdays";
  if (sameDays(days, WEEKEND_DAYS)) return "weekends";
  return "custom";
}

function minutesOf(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return ((hour || 0) * 60) + (minute || 0);
}

function timeFallsInQuietHours(time, start, end) {
  if (!time || !start || !end) return false;
  const current = minutesOf(time);
  const from = minutesOf(start);
  const to = minutesOf(end);
  if (from === to) return false;
  return from > to ? current >= from || current < to : current >= from && current < to;
}

export default function AssistantNotificationAdmin() {
  const { lang } = useLanguage();
  const tr = lang !== "en";
  const [config,     setConfig]     = useState(null);
  const [origConfig, setOrigConfig] = useState(null); // varsayılan için orijinal
  const [stats,      setStats]      = useState(null);
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [running,    setRunning]    = useState("");
  const [durum,      setDurum]      = useState("");
  const logListRef = useRef(null);

  const yukle = useCallback(async () => {
    try {
      setLoading(true);
      const [cfg, st, lg] = await Promise.all([
        aiNotifConfigGetir(),
        aiNotifStatsGetir(),
        aiNotifLoglarGetir(100),
      ]);
      if (cfg) { setConfig(cfg); setOrigConfig(cfg); }
      if (st)  setStats(st);
      if (lg)  setLogs(lg);
    } catch (e) {
      setDurum("❌ Yüklenemedi: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { yukle(); }, [yukle]);

  const kaydet = async () => {
    if (!config) return;
    if (config.dailySummary?.enabled && !(config.dailySummary?.days || []).length) {
      setDurum(tr
        ? "❌ Sabah özeti için en az bir gönderim günü seçin."
        : "❌ Select at least one delivery day for the morning summary.");
      return;
    }
    setSaving(true); setDurum("");
    try {
      await aiNotifConfigKaydet(config);
      setOrigConfig(config);
      setDurum("✓ Ayarlar kaydedildi");
      // stats yenile
      const st = await aiNotifStatsGetir().catch(() => null);
      if (st) setStats(st);
    } catch (e) {
      setDurum("❌ Kaydedilemedi: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const varsayilanadon = () => {
    setConfig(JSON.parse(JSON.stringify(DEFAULT_CONFIG)));
    setDurum("Varsayılan ayarlar yüklendi. Kaydet butonuna bas.");
  };

  const calistir = async (type, fn) => {
    setRunning(type); setDurum("");
    try {
      const r = await fn();
      setDurum(`✓ ${TYPE_LABELS[type]}: ${r?.result?.sent ?? 0} gönderildi, ${r?.result?.skipped ?? 0} atlandı`);
      const [st, lg] = await Promise.all([
        aiNotifStatsGetir().catch(() => null),
        aiNotifLoglarGetir(100).catch(() => null),
      ]);
      if (st) setStats(st);
      if (lg) setLogs(lg);
    } catch (e) {
      setDurum("❌ Hata: " + e.message);
    } finally {
      setRunning("");
    }
  };

  const set = (path, value) => {
    setConfig(prev => {
      const parts  = path.split(".");
      const result = { ...prev };
      let obj = result;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return result;
    });
  };

  const toggleDay = (path, day) => {
    const current = (config?.dailySummary?.days || []);
    const next = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
    set(path, next);
  };

  const setFrequency = (frequency) => {
    if (frequency === "daily") set("dailySummary.days", [...ALL_DAYS]);
    if (frequency === "weekdays") set("dailySummary.days", [...WEEKDAYS]);
    if (frequency === "weekends") set("dailySummary.days", [...WEEKEND_DAYS]);
    if (frequency === "custom" && scheduleFrequency(config?.dailySummary?.days) !== "custom") {
      set("dailySummary.days", []);
    }
  };

  const summaryDays = config?.dailySummary?.days || [];
  const frequency = scheduleFrequency(summaryDays);
  const dayOptions = tr ? DAYS_TR : DAYS_EN;
  const quietConflict = timeFallsInQuietHours(
    config?.dailySummary?.time,
    config?.global?.quietHoursStart,
    config?.global?.quietHoursEnd
  );

  if (loading) return <div style={{ padding: 20, color: "var(--text-muted)" }}>AI Asistan bildirimleri yükleniyor...</div>;
  if (!config)  return <div style={{ padding: 20, color: "var(--accent-red)" }}>Config yüklenemedi.</div>;

  return (
    <div className="aiNotifAdmin">
      {/* ── Başlık + Master Toggle ── */}
      <div className="aiNotifHeader">
        <div>
          <strong>🤖 AI Asistan Bildirimleri</strong>
          <p>Kullanıcılara samimi, kişiselleştirilmiş yapay zeka bildirimleri gönderir. Anti-spam sistemi tekrarları önler.</p>
        </div>
        <Toggle on={config.enabled} onChange={v => set("enabled", v)} />
      </div>

      {/* ── İstatistikler ── */}
      {stats && (
        <div className="aiNotifStats">
          <div className="aiStatBox">
            <strong>{stats.todaySent ?? 0}</strong>
            <span>Bugün gönderildi</span>
          </div>
          <div className="aiStatBox">
            <strong>{stats.weekSent ?? 0}</strong>
            <span>Bu hafta</span>
          </div>
          {Object.entries(stats.byType || {}).map(([type, c]) => (
            <div className="aiStatBox" key={type}>
              <strong>{c}</strong>
              <span>{TYPE_LABELS[type] || type}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Global Ayarlar ── */}
      <div className="aiNotifSection">
        <h4>🌐 Global Ayarlar</h4>
        <div className="aiNotifGrid">
          <div className="inputLabelGroup">
            <label className="darkLabel">Günlük Limit (kullanıcı başı)</label>
            <NumberInput value={config.global?.dailyLimitPerUser} onChange={v => set("global.dailyLimitPerUser", v)} min={1} max={10} />
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Sessiz Başlangıç</label>
            <TimeInput value={config.global?.quietHoursStart} onChange={v => set("global.quietHoursStart", v)} />
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Sessiz Bitiş</label>
            <TimeInput value={config.global?.quietHoursEnd} onChange={v => set("global.quietHoursEnd", v)} />
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Saat Dilimi</label>
            <select className="darkInput" value={config.global?.timezone || "recipient_local"}
              onChange={e => set("global.timezone", e.target.value)} style={{ width: 210 }}>
              <option value="recipient_local">{tr ? "Kullanıcının yerel saati" : "Recipient local time"}</option>
              <option value="Europe/Istanbul">Europe/Istanbul</option>
            </select>
          </div>
        </div>
        <p className="aiNotifDesc">{tr
          ? "Yerel saat seçildiğinde gönderim ve sessiz saatler her alıcının cihaz saat dilimine göre uygulanır. Saat dilimi bilinmiyorsa Europe/Istanbul kullanılır."
          : "When local time is selected, delivery and quiet hours use each recipient’s device time zone. Europe/Istanbul is used when unavailable."}</p>
      </div>

      {/* ── Sabah Özeti ── */}
      <div className="aiNotifSection aiNotifMorningSection">
        <div className="aiNotifSectionHead">
          <div>
            <span className="aiNotifSectionEyebrow">{tr ? "ZAMANLANMIŞ BİLDİRİM" : "SCHEDULED NOTIFICATION"}</span>
            <h4>{tr ? "☀️ Sabah Özeti" : "☀️ Morning Summary"}</h4>
          </div>
          <Toggle on={config.dailySummary?.enabled} onChange={v => set("dailySummary.enabled", v)} />
        </div>
        <p className="aiNotifDesc">{tr
          ? "Aile Asistanı seçtiğiniz gün ve saatte o günün önemli konularını özetler. Özetlenecek konu yoksa bildirim gönderilmez."
          : "The Family Assistant summarizes important items on the selected days and time. Nothing is sent when there is nothing to summarize."}</p>
        <div className={`aiMorningControls${config.dailySummary?.enabled ? "" : " disabled"}`}>
          <div className="inputLabelGroup">
            <label className="darkLabel">{tr ? "Gönderim Saati" : "Delivery time"}</label>
            <TimeInput value={config.dailySummary?.time} onChange={v => set("dailySummary.time", v)} />
          </div>
          <div className="inputLabelGroup aiFrequencyField">
            <label className="darkLabel">{tr ? "Gönderim Sıklığı" : "Frequency"}</label>
            <select
              className="darkInput"
              value={frequency}
              onChange={e => setFrequency(e.target.value)}
            >
              <option value="daily">{tr ? "Her gün" : "Every day"}</option>
              <option value="weekdays">{tr ? "Hafta içi" : "Weekdays"}</option>
              <option value="weekends">{tr ? "Hafta sonu" : "Weekends"}</option>
              <option value="custom">{tr ? "Özel günler" : "Custom days"}</option>
            </select>
          </div>
        </div>
        {frequency === "custom" && (
          <div className="aiDayPicker" aria-label={tr ? "Gönderim günleri" : "Delivery days"}>
          {dayOptions.map(d => (
            <button key={d.key}
              type="button"
              disabled={!config.dailySummary?.enabled}
              className={`aiDayBtn${summaryDays.includes(d.key) ? " active" : ""}`}
              onClick={() => toggleDay("dailySummary.days", d.key)}>
              {d.label}
            </button>
          ))}
          </div>
        )}
        <div className={`aiScheduleSummary${quietConflict ? " warning" : ""}`}>
          <span className="aiScheduleSummaryIcon">{quietConflict ? "⚠️" : "✓"}</span>
          <div>
            <strong>{quietConflict
              ? (tr ? "Seçilen saat sessiz saatlerle çakışıyor" : "Selected time overlaps quiet hours")
              : (tr ? "Aktif gönderim planı" : "Active delivery schedule")}</strong>
            <span>{quietConflict
              ? (tr
                ? `Bildirim ${config.global?.quietHoursEnd || "08:00"} saatine ertelenir. Tam seçtiğiniz saatte gitmesi için gönderim saatini sessiz saatlerin dışına alın veya Global Ayarlar'daki sessiz saatleri değiştirin.`
                : `Delivery will be postponed until ${config.global?.quietHoursEnd || "08:00"}. Choose a time outside quiet hours or update quiet hours in Global Settings.`)
              : (tr
                ? `${frequency === "daily" ? "Her gün" : frequency === "weekdays" ? "Hafta içi" : frequency === "weekends" ? "Hafta sonu" : `${summaryDays.length} seçili gün`}, saat ${config.dailySummary?.time || "08:30"}`
                : `${frequency === "daily" ? "Every day" : frequency === "weekdays" ? "Weekdays" : frequency === "weekends" ? "Weekends" : `${summaryDays.length} selected days`} at ${config.dailySummary?.time || "08:30"}`)}</span>
          </div>
        </div>
        <button className="darkSecondaryBtn sm" disabled={running === "daily_summary"}
          onClick={() => calistir("daily_summary", aiNotifSabahOzetiCalistir)}>
          {running === "daily_summary" ? "Çalışıyor..." : "▶ Şimdi Çalıştır"}
        </button>
      </div>

      {/* ── Acil Uyarı ── */}
      <div className="aiNotifSection">
        <div className="aiNotifSectionHead">
          <h4>🚨 Acil Uyarı</h4>
          <Toggle on={config.urgentAlerts?.enabled} onChange={v => set("urgentAlerts.enabled", v)} />
        </div>
        <p className="aiNotifDesc">Geciken görev, ödeme ve süresi dolmuş belgeleri haber verir. İçerik aynıysa tekrar göndermez.</p>
        <div className="aiNotifGrid">
          <div className="inputLabelGroup">
            <label className="darkLabel">Kontrol Sıklığı (saat)</label>
            <NumberInput value={config.urgentAlerts?.checkIntervalHours} onChange={v => set("urgentAlerts.checkIntervalHours", v)} min={1} max={12} />
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Günlük Maks.</label>
            <NumberInput value={config.urgentAlerts?.maxPerDay} onChange={v => set("urgentAlerts.maxPerDay", v)} min={1} max={5} />
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Cooldown (saat)</label>
            <NumberInput value={config.urgentAlerts?.cooldownHours} onChange={v => set("urgentAlerts.cooldownHours", v)} min={6} max={48} />
          </div>
        </div>
        <button className="darkSecondaryBtn sm" disabled={running === "urgent_alert"}
          onClick={() => calistir("urgent_alert", aiNotifAcilUyariCalistir)}>
          {running === "urgent_alert" ? "Çalışıyor..." : "▶ Şimdi Çalıştır"}
        </button>
      </div>

      {/* ── Haftalık Özet ── */}
      <div className="aiNotifSection">
        <div className="aiNotifSectionHead">
          <h4>📊 Haftalık Özet</h4>
          <Toggle on={config.weeklyDigest?.enabled} onChange={v => set("weeklyDigest.enabled", v)} />
        </div>
        <p className="aiNotifDesc">Geçen haftanın performansı ve önümüzdeki haftanın özeti. Haftada 1 kez gönderilir.</p>
        <div className="aiNotifGrid">
          <div className="inputLabelGroup">
            <label className="darkLabel">Gün</label>
            <select className="darkInput" value={config.weeklyDigest?.day || "sun"}
              onChange={e => set("weeklyDigest.day", e.target.value)}>
              {WEEK_DAYS_TR.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Saat</label>
            <TimeInput value={config.weeklyDigest?.time} onChange={v => set("weeklyDigest.time", v)} />
          </div>
        </div>
        <button className="darkSecondaryBtn sm" disabled={running === "weekly_digest"}
          onClick={() => calistir("weekly_digest", aiNotifHaftalikOzetCalistir)}>
          {running === "weekly_digest" ? "Çalışıyor..." : "▶ Şimdi Çalıştır"}
        </button>
      </div>

      {/* ── Akıllı İpucu ── */}
      <div className="aiNotifSection">
        <div className="aiNotifSectionHead">
          <h4>💡 Akıllı İpucu</h4>
          <Toggle on={config.smartInsights?.enabled} onChange={v => set("smartInsights.enabled", v)} />
        </div>
        <p className="aiNotifDesc">Birden fazla modülü ilişkilendiren cross-module gözlemler. Gerçek AI hissini yaratan bildirim türü.</p>
        <div className="aiNotifGrid">
          <div className="inputLabelGroup">
            <label className="darkLabel">{tr ? "Gönderim Saati" : "Delivery time"}</label>
            <TimeInput value={config.smartInsights?.time} onChange={v => set("smartInsights.time", v)} />
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Haftalık Maks.</label>
            <NumberInput value={config.smartInsights?.maxPerWeek} onChange={v => set("smartInsights.maxPerWeek", v)} min={1} max={7} />
          </div>
          <div className="inputLabelGroup">
            <label className="darkLabel">Cooldown (saat)</label>
            <NumberInput value={config.smartInsights?.cooldownHours} onChange={v => set("smartInsights.cooldownHours", v)} min={12} max={96} />
          </div>
        </div>
        <button className="darkSecondaryBtn sm" disabled={running === "smart_insight"}
          onClick={() => calistir("smart_insight", aiNotifAkillıIpucuCalistir)}>
          {running === "smart_insight" ? "Çalışıyor..." : "▶ Şimdi Çalıştır"}
        </button>
      </div>

      {/* ── Durum mesajı ── */}
      {durum && (
        <div className={durum.startsWith("✓") ? "profilSuccess" : durum.startsWith("Varsayılan") ? "profilSuccess" : "profilError"}>{durum}</div>
      )}

      {/* ── Kaydet + Varsayılan ── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <button className="primaryButton" onClick={kaydet} disabled={saving}>
          {saving ? "Kaydediliyor..." : "💾 Ayarları Kaydet"}
        </button>
        <button className="darkSecondaryBtn" onClick={varsayilanadon}>
          🔄 Varsayılana Dön
        </button>
        <button className="darkSecondaryBtn" onClick={yukle}>
          ↻ Yenile
        </button>
      </div>

      {/* ── Son Gönderimler (her zaman açık, 5 görünür, kaydırılabilir) ── */}
      <div className="aiNotifSection">
        <div className="aiNotifSectionHead">
          <h4>📋 Son Gönderimler</h4>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{logs.length} kayıt</span>
        </div>
        {logs.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Henüz gönderim yok.</p>
        ) : (
          <div
            ref={logListRef}
            className="aiNotifLogTable"
            style={{ maxHeight: 320, overflowY: "auto" }}
          >
            {logs.map((log, i) => (
              <div key={log.id || i} className="aiNotifLogRow">
                <span className="aiLogType">{TYPE_LABELS[log.notification_type] || log.notification_type}</span>
                <div className="aiLogContent">
                  <strong>{log.title}</strong>
                  <p>{log.body}</p>
                  <small>{log.family_name} · {log.user_email} · {new Date(log.sent_at).toLocaleString("tr-TR")}</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
